import { SkeletonTableItemComponent } from "./skeletons/skeleton-table-item/skeleton-table-item.component";
import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { SharedModule } from "src/shared/shared.module";
import { CourseDescriptionComponent } from "../component/course-description/course-description.component";
import { firstValueFrom, forkJoin, lastValueFrom, map, Observable } from "rxjs";
import { CourseService } from "src/app/services/api/courses.service";
import { FormBuilder, FormGroup } from "@angular/forms";
import { environment } from "src/environments/environment";
import { PipesModule } from "src/utils/pipes/pipes.module";
import { StudentCourseService } from "src/app/services/api/student-course.service";
import { CartService } from "src/app/services/api/carts.service";
import { Clipboard } from "@angular/cdk/clipboard";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { PageInfoService } from "src/app/_metronic/layout";
import { AuthUtilitiesService } from "../../auth/services/auth-utilities.service";
import { AuthService } from "../../auth";
import { AuthState } from "../../auth/models/auth.model";
import { RoleID } from "src/utils/enum/role.enum";
import { SkeletonDetailsComponent } from "./skeletons/skeleton-details/skeleton-details.component";
import { SkeletonRightSideComponent } from "./skeletons/skeleton-right-side/skeleton-right-side.component";
import { CoursePricing, resolveCoursePricing } from "src/utils/course-pricing";

@Component({
  selector: "app-course-detail",
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    CourseDescriptionComponent,
    PipesModule,
    TranslateModule,
    SkeletonDetailsComponent,
    SkeletonRightSideComponent,
    SkeletonTableItemComponent,
  ],
  templateUrl: "./course-detail.component.html",
  styleUrl: "./course-detail.component.scss",
})
export class CourseDetailComponent implements OnInit {
  #route = inject(ActivatedRoute);
  #fb = inject(FormBuilder);
  #courseService = inject(CourseService);
  #cartService = inject(CartService);
  #toastService = inject(ToastService);
  #clipboard = inject(Clipboard);
  #translate = inject(TranslateService);

  #pageInfoService = inject(PageInfoService);
  #loginService = inject(AuthUtilitiesService);
  #authService = inject(AuthService);
  #router = inject(Router);
  #studentCourseService = inject(StudentCourseService);

  course_id = signal<string | undefined>(undefined);

  course_info: any = {};
  course_review: any = {
    data: [],
    limit: 4,
    offset: 0,
    page: 1,
    total: 5,
    total_page: 2,
  };
  form: FormGroup;

  isLoading = signal(true);
  /** The id in the URL does not name a course anybody can see. */
  notFound = signal(false);
  tableLoading = signal(true);
  trialLoading = signal(false);
  /** Briefly true after a successful copy, so the button itself confirms the
   *  action instead of leaving that to the toast alone. */
  linkCopied = signal(false);

  pricing = signal<CoursePricing>({
    hasDiscount: false,
    price: 0,
    originalPrice: 0,
  });

  fileUrl = environment.apiFileUrl;
  currentUser = this.#authService.currentUser;

  queryParam: string | null = null;

  constructor() {
    this.#route.paramMap
      .pipe(map((p) => p.get("course_id") ?? ""))
      .subscribe((id) => this.course_id.set(id));
    this.queryParam = this.#route.snapshot.queryParamMap.get("course");
    if (this.queryParam) {
      this.course_id.set(this.queryParam);
    }
  }

  ngOnInit(): void {
    this.currentUser = this.#authService.currentUser;
    this.buildForm();
    this.fetchData();
  }

  buildForm() {
    this.form = this.#fb.group({
      datatable: true,
      page: 1,
      limit: 5,
      keywords: null,
      course_id: this.course_id(),
      offset: 0,
      order: "id desc",
    });
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  async fetchData() {
    forkJoin([
      this.#courseService.getCoursePublic(
        +this.course_id()!,
      ) as Observable<any>,
      // this.#courseService.getCourseFiles({
      //   course_id: +this.course_id()!,}) as Observable<any>,
      this.#courseService.getCourseReview(
        this.form.getRawValue(),
      ) as Observable<any>,
    ]).subscribe({
      next: ([course_info, course_review]) => {
        this.course_info = course_info;
        this.setBreadCrumb(course_info);
        this.course_review = course_review;
        this.pricing.set(resolveCoursePricing(course_info));
      },
      // RxJS never calls `complete` after an error, so swallowing it here left
      // `isLoading` true forever: /courses/999 rendered seventy skeleton blocks
      // and stayed that way. Say the course is gone and offer the catalogue.
      error: () => {
        this.notFound.set(true);
        this._setLoading(false);
        this.tableLoading.update((_) => false);
      },
      complete: () => {
        this._setLoading(false);
        this.tableLoading.update((_) => false);
      },
    });
  }

  /**
   * Copy this course's address.
   *
   * Both reference builds had share controls here; theirs were Facebook, Line
   * and Twitter, which a de-branded demo has no business carrying. Copy-link is
   * the part that is not a social integration — it is how somebody showing this
   * demo sends a colleague to a specific course.
   */
  copyLink(): void {
    this.#clipboard.copy(window.location.href);
    this.#toastService.toastSuccess(this.#translate.instant("APP.COPY_LINK_DONE"));
    this.linkCopied.set(true);
    setTimeout(() => this.linkCopied.set(false), 1800);
  }

  async addCart(course_id: number, is_enroll: boolean = false) {
    try {
      const result = await lastValueFrom(this.#cartService.addCart(course_id));
      await this.#cartService.getUserCart();
      if (is_enroll) {
        this.openCartDrawer();
      }
      this.#toastService.toastSuccess(
        this.#translate.instant("APP.ADD_CART_SUCCESS"),
      );
    } catch (error: any) {
      let error_message = error.error.errors;
      if (error.status === 401) {
        error_message = this.#translate.instant("AUTH.UNAUTHORIZED_ALERT");
        setTimeout(() => {
          this.openSignInDialog();
        }, 600);
      }
      this.#toastService.toastError(error_message);
    }
  }
  /**
   * Start a free trial and go straight into the classroom.
   *
   * Deliberately not routed through the cart: a trial is not a purchase, and
   * making someone check out for something free is the fastest way to lose them.
   */
  async startTrial(course_id: number) {
    if (this.trialLoading()) return;

    // Ask for the account BEFORE calling the API. Firing the request while
    // signed out only earns a 401 and a red toast the visitor did nothing to
    // deserve — the honest answer to "ทดลองเรียน" while logged out is the
    // sign-up dialog, same as eazycal.
    // currentUser() holds the whole auth envelope, not the user row — the role
    // sits at role_id on the envelope (and again under .user.role.id). Reading
    // it as user.role.id yields undefined, which silently fails every check.
    const auth = this.currentUser() as any;
    if (!auth) {
      this.openSignInDialog();
      return;
    }
    const roleId = auth.role_id ?? auth.user?.role?.id;
    if (roleId !== RoleID.MEMBER) {
      this.#toastService.toastError(
        this.#translate.instant("APP.TRIAL_STUDENT_ONLY"),
      );
      return;
    }

    this.trialLoading.set(true);
    try {
      const enrolment: any = await lastValueFrom(
        this.#studentCourseService.startTrial(course_id),
      );
      this.#toastService.toastSuccess(this.#translate.instant("APP.TRIAL_STARTED"));
      this.#router.navigate(["/online-class", enrolment.id, "watch"]);
    } catch (error: any) {
      if (error?.status === 401) {
        // Backstop: the token expired between page load and the click.
        this.openSignInDialog();
      } else {
        // The API refuses a second concurrent trial and a course with no preview
        // video, and says why — pass that through rather than a generic failure.
        this.#toastService.toastError(error?.error?.errors || "เริ่มทดลองเรียนไม่สำเร็จ");
      }
    } finally {
      this.trialLoading.set(false);
    }
  }

  onTableChange(event: Event) {
    this.form.patchValue(event);
  }
  openCartDrawer() {
    const cartBtn = document.getElementById(
      "kt_drawer_cart_toggle",
    ) as HTMLElement;
    cartBtn?.click();
  }
  /**
   * Sign in, not sign up. openLoginDialog() below opens the REGISTER tab, which
   * is right for the "ยังไม่มีบัญชี" link but wrong for someone who already has
   * one and just wants to try a course — they would have to notice the back
   * arrow to get to the form they wanted. The dialog links to registration.
   */
  openSignInDialog() {
    this.#authService.updateAuthState(AuthState.UNAUTHORIZED);
    this.#loginService.openLoginDialog();
  }
  openRegisterDialog() {
    this.#authService.updateAuthState(AuthState.REGISTER);
    this.#loginService.openLoginDialog();
  }

  async setBreadCrumb(info: any) {
    this.#pageInfoService.updateTitle(info.name);

    if (this.queryParam) {
      var bc = this.#pageInfoService.breadcrumbs.value;
      var newBc = [
        {
          isActive: false,
          isSeparator: false,
          path: `/courses/category/${info.course_category_id}`,
          title: info.course_category_name,
        },
        {
          isActive: false,
          isSeparator: true,
          path: "",
          title: "",
        },
      ];
      bc.push(...newBc);

      // this.#pageInfoService.setBreadcrumbs([]);
    }
  }
}
